import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import People from "@/pages/People";
import Teams from "@/pages/Teams";
import Schedule from "@/pages/Schedule";
import Academics from "@/pages/Academics";
import Wellness from "@/pages/Wellness";
import Scanner from "@/pages/Scanner";
import Checkins from "@/pages/Checkins";
import Behavior from "@/pages/Behavior";
import RegisterInvite from "@/pages/RegisterInvite";
import Messages from "@/pages/Messages";
import Files from "@/pages/Files";
import Profile from "@/pages/Profile";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ForceChangePassword from "@/pages/ForceChangePassword";
import PendingApproval from "@/pages/PendingApproval";
import SuperAdmin from "@/pages/SuperAdmin";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#737373]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.status === "pending") return <PendingApproval />;
  if (user.must_change_password) return <ForceChangePassword />;
  if (user.role === "super_admin") return <Navigate to="/superadmin" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return <AppShell>{children}</AppShell>;
}

function SuperProtected({ children }) {
  const { user, loading, isSuper } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#737373]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForceChangePassword />;
  if (!isSuper) return <Navigate to="/app" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#737373]">Loading…</div>;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicOnly><AuthPage /></PublicOnly>} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/register/:token" element={<RegisterInvite />} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/app" element={<Protected><Dashboard /></Protected>} />
            <Route path="/app/people" element={<Protected roles={["athletic_director"]}><People /></Protected>} />
            <Route path="/app/teams" element={<Protected roles={["athletic_director", "coach"]}><Teams /></Protected>} />
            <Route path="/app/schedule" element={<Protected roles={["athletic_director", "coach", "athlete", "parent"]}><Schedule /></Protected>} />
            <Route path="/app/academics" element={<Protected><Academics /></Protected>} />
            <Route path="/app/wellness" element={<Protected roles={["athlete", "athletic_director", "coach"]}><Wellness /></Protected>} />
            <Route path="/app/scan" element={<Protected roles={["athlete"]}><Scanner /></Protected>} />
            <Route path="/app/checkins" element={<Protected roles={["athlete", "athletic_director", "coach", "parent"]}><Checkins /></Protected>} />
            <Route path="/app/behavior" element={<Protected roles={["teacher", "athletic_director", "coach", "parent"]}><Behavior /></Protected>} />
            <Route path="/app/messages" element={<Protected><Messages /></Protected>} />
            <Route path="/app/files" element={<Protected><Files /></Protected>} />
            <Route path="/app/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/superadmin" element={<SuperProtected><SuperAdmin /></SuperProtected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="top-center" toastOptions={{ style: { background: "#141414", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", borderRadius: 2 } }} />
      </AuthProvider>
    </div>
  );
}

export default App;
